import {
  DocumentSectionSpec,
  isProtocolTemplate,
  SectionOption,
} from '@/entities/templates';

import { extractSlotNames } from './slotExtractor';

const normalizeOptionalText = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

// A slot with an empty value list is a free slot: the generator fills it
// with its own text, so it is kept as an empty list instead of being dropped.
const sanitizeSlots = (slots: SectionOption['Slots']): SectionOption['Slots'] =>
  Object.fromEntries(
    Object.entries(slots).map(([key, { Values }]) => [
      key,
      { Values: Values.map((value) => value.trim()).filter(Boolean) },
    ]),
  );

// Only slots of the option keep an instruction: its `Slots` entries and the
// placeholders of its text, which are free slots without an entry.
const sanitizeSlotInstructions = (
  option: SectionOption,
): SectionOption['SlotInstructions'] => {
  const slots = [
    ...Object.keys(option.Slots),
    ...extractSlotNames(option.Text),
  ];
  return Object.fromEntries(
    Object.entries(option.SlotInstructions).flatMap(
      ([slot, instruction]): [string, string][] => {
        const text = instruction.trim();
        return text && slots.includes(slot) ? [[slot, text]] : [];
      },
    ),
  );
};

const sanitizeOption = (option: SectionOption): SectionOption => ({
  ...option,
  Text: option.Text.trim(),
  Instructions: normalizeOptionalText(option.Instructions),
  SlotInstructions: sanitizeSlotInstructions(option),
  Slots: sanitizeSlots(option.Slots),
});

// Section types and flags pass through as they are: scribe applies its own
// defaults to the ones left empty.
export const sanitizeSectionForSubmit = (
  section: DocumentSectionSpec,
  documentType: string,
): DocumentSectionSpec => ({
  ...section,
  Code: section.Code.trim(),
  Title: section.Title.trim(),
  Category: normalizeOptionalText(section.Category),
  Instructions: normalizeOptionalText(section.Instructions),
  Options: isProtocolTemplate(documentType)
    ? section.Options.map(sanitizeOption).filter(
        (option) => option.Text.length > 0,
      )
    : [],
});
